// app/composables/useSecureDownload.ts - 统一的安全下载封装（直连 + 一次刷新）
import { useTranslationApi } from '~/composables/useTranslationApi'
import { TranslateStatus } from '~/types/translation'
import { useMyPageStore } from '~/stores/myPage'
import type { Int64Id } from '~/types/translation'
import { normalizeInt64IdOrThrow } from '~/utils/int64Id.js'

function openInNewTab(url: string) {
  // 使用 a[download] 或 window.open 触发下载
  try {
    const win = window.open(url, '_blank', 'noopener')
    if (!win) throw new Error('popup blocked')
  } catch {
    const a = document.createElement('a')
    a.href = url
    a.rel = 'noopener'
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
}

export function useSecureDownload() {
  const api = useTranslationApi()
  const myPage = useMyPageStore()

  const downloadByUrl = async (url: string) => {
    // 直连：假定后端返回的是可直接下载的预签名直链
    openInNewTab(url)
  }

  const downloadByRecordId = async (recordId: Int64Id) => {
    const normalizedRecordId = normalizeInt64IdOrThrow(recordId, 'recordId')
    const r = await api.queryTaskStatus(normalizedRecordId)
    if (!r.isEnd || r.status !== TranslateStatus.Success || !r.downloadUrl) {
      throw new Error('文件未就绪或无下载链接')
    }
    openInNewTab(r.downloadUrl)
  }

  const downloadByTask = async (task: { id: string; originalFileName: string; downloadUrl?: string; recordId?: Int64Id }) => {
    // 首选已有直链
    if (task.downloadUrl) {
      openInNewTab(task.downloadUrl)
      return
    }

    const rawRecordId = task.recordId ?? task.id
    const rid = normalizeInt64IdOrThrow(rawRecordId, 'recordId')

    // 自愈：刷新一次 downloadUrl（快速失败，不吞错）
    const url = await myPage.refreshDownloadUrl(rid)
    if (url) {
      openInNewTab(url)
      return
    }

    // 若仍无可用链接（极端情况）
    throw new Error('下载链接不可用，请稍后重试')
  }

  return { downloadByTask, downloadByRecordId, downloadByUrl }
}
